import React, { createContext, useContext, useReducer, ReactNode, useEffect, useState } from 'react';

interface CartItem {
  id: string;
  name: string;
  price: number;
  discountedPrice?: number | null;
  quantity: number;
  image?: string;
  stock?: number; // Add stock to cart item
}

interface CartState {
  items: CartItem[];
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: CartItem }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_CART'; payload: CartItem[] };

const CartContext = createContext<{
  state: CartState;
  dispatch: React.Dispatch<CartAction>;
} | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    case 'SET_CART':
      return {
        ...state,
        items: action.payload
      };

    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize cart state with saved cart from localStorage
  const [initialState] = useState<CartState>(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        return { items: JSON.parse(savedCart) };
      }
    } catch (error) {
      console.error('Error parsing cart from localStorage:', error);
    }
    return { items: [] };
  });

  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state.items));
  }, [state.items]);

  return (
    <CartContext.Provider value={{ state, dispatch }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  const { state, dispatch } = context;

  // Helper function to add items to cart
  const addToCart = (item: CartItem) => {
    dispatch({
      type: 'ADD_ITEM',
      payload: item
    });
  };

  return {
    state,
    dispatch,
    addToCart
  };
};

export const useCartItems = () => {
  const { state } = useCart();
  return state.items;
};

export const useCartTotal = () => {
  const items = useCartItems();
  return items.reduce((total, item) => {
    const price = item.discountedPrice ?? item.price;
    return total + (price * item.quantity);
  }, 0);
};

export const useCartCount = () => {
  const items = useCartItems();
  return items.reduce((count, item) => count + item.quantity, 0);
};